import { ref, type Ref } from 'vue'
import type { CoreErrorMessages, CoreRule } from './types'

/**
 * Rules validation helpers.
 *
 * This module intentionally stays small:
 * - `errorMessages` is always the primary integration surface.
 * - `rules` exist for simple cases and demos (required, min/max length, etc.).
 */
export function normalizeErrorMessages(messages: CoreErrorMessages): string[] {
  if (messages === null || messages === undefined) return []
  if (Array.isArray(messages)) return messages.filter((m): m is string => typeof m === 'string' && m.trim().length > 0)
  if (typeof messages === 'string' && messages.trim()) return [messages]
  return []
}

export async function evaluateRules<T>(value: T, rules: CoreRule<T>[] | undefined): Promise<string[]> {
  if (!rules?.length) return []
  const results = await Promise.all(rules.map((rule) => rule(value)))
  return results.filter((res): res is string => res !== true)
}

export function useRulesValidation<T>(options: {
  value: Ref<T>
  rules: Ref<CoreRule<T>[] | undefined>
}) {
  const ruleErrors = ref<string[]>([])

  const validateRules = async () => {
    const next = await evaluateRules(options.value.value, options.rules.value)
    ruleErrors.value = next
    return next
  }

  const resetRules = () => {
    ruleErrors.value = []
  }

  return { ruleErrors, validateRules, resetRules }
}
